import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { EmptyResults } from '@agendapro/emerald-icons';
import { GlobalContentLayout } from '@/components';
import { PageProvider } from '@/context/PageContext';
import { Paragraph, ViewMore, SearchBar, Skeleton } from '@/UI';
import { validateIfTextContainsStr } from '@/UI/Utils';
import useCheckRefScrolled from '@/hooks/useCheckRefScrolled';
import { useIframeRouter } from '@/hooks/useIframeRouter';
import { LocationsProps } from './types';
import { LocationsResponse, useLocations } from '@/services/locations';
import { CardLocalItem } from './components/CardLocalItem';
import CardLocalItemSkeleton from './components/CardLocalItemSkeleton';
import { RM_PREFIX, RM_GROUP_CARD_ID } from './constants';
import { groupRmLocations as groupRmLocationsFn } from './groupRmLocations';
import { filterHiddenLocations } from './filterHiddenLocations';

import * as St from './Locations.styles';

const Locations: FC<LocationsProps> = ({ companyOverview, companySlug, queryParams }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { countryCode } = router.query as { countryCode: string };
  const { routerPush } = useIframeRouter();
  const isSalfa = !!companyOverview?.customMinisite;
  const searchRef = useRef(null);
  const [search, setSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<LocationsResponse | undefined>();
  const [filteredLocations, setFilteredLocations] = useState<{
    data: LocationsResponse[];
    dataFiltered: LocationsResponse[];
  }>({
    data: [],
    dataFiltered: [],
  });

  const groupRmLocations = (locations: LocationsResponse[]) => groupRmLocationsFn(locations, isSalfa, t);

  const { data, isLoading } = useLocations({
    companyId: companyOverview?.id,
    onSuccess: (data) => {
      const validLocations = filterHiddenLocations(data?.filter((location) => location.validLocation) || [], isSalfa);

      if (validLocations.length === 1) {
        const countryCode = window.location.pathname.split('/')[1];
        const redirectUrl = `/${countryCode}/sucursal/${validLocations[0].id}/${queryParams || ''}`;

        router.push(redirectUrl);
        return;
      }

      const displayLocations = groupRmLocations(validLocations);

      setFilteredLocations({ data: displayLocations, dataFiltered: displayLocations });
      setSelectedLocation(displayLocations[0]);
    },
  });

  const locations = filterHiddenLocations(data?.filter((location) => location.validLocation) || [], isSalfa);
  const scrolled = useCheckRefScrolled(isLoading ? null : searchRef);

  const onSearch = (value) => {
    setSearch(value);
    const filterItems = ['locationName', 'address'];
    const allLocations = locations || [];
    const displayLocations = groupRmLocations(allLocations);

    const matchesFilter = (location: LocationsResponse) =>
      validateIfTextContainsStr(location[filterItems[0]], value) ||
      validateIfTextContainsStr(location[filterItems[1]], value);

    let filtered = displayLocations.filter(matchesFilter);

    if (isSalfa && value && !filtered.some((l) => l.id === RM_GROUP_CARD_ID)) {
      const rmMatch = allLocations.some((l) => l.locationName.startsWith(RM_PREFIX) && matchesFilter(l));

      if (rmMatch) {
        const rmGroupCard = displayLocations.find((l) => l.id === RM_GROUP_CARD_ID);

        if (rmGroupCard) {
          filtered = [rmGroupCard, ...filtered];
        }
      }
    }

    setFilteredLocations({
      data: displayLocations,
      dataFiltered: filtered,
    });
  };
  const getFiltering = (isRef: boolean) => (
    <St.SearchBarContainer ref={isRef ? searchRef : null} scrolled={!!scrolled} isRef={isRef}>
      <SearchBar
        className="locationsSearchBar"
        value={search}
        onChange={onSearch}
        placeholder={`${t('FIND_BRANCH_OFFICE')}`}
        totalLocations={filteredLocations.data.length}
        totalFilteredLocations={filteredLocations.dataFiltered.length}
      />
    </St.SearchBarContainer>
  );

  return (
    <>
      <PageProvider
        companySlug={companySlug}
        companyOverview={companyOverview}
        nextSeo={{
          title: `${companyOverview.name} | ${t('ONLINE_BOOKING')}`,
          description: `${t('SEO.LOCATIONS_DESCRIPTION_FIRST')} ${companyOverview.name} ${t(
            'SEO.LOCATIONS_DESCRIPTION_SECOND',
          )}`,
        }}
        queryParams={queryParams}
      >
        <St.Container hasDescription={!!companyOverview?.description}>
          <GlobalContentLayout>
            <St.CardsAndMapWrapper>
              {companyOverview?.description && (
                <St.CompanyDescriptionWrapper>
                  <ViewMore textContent={companyOverview.description} maxNumOfLines={4} />
                </St.CompanyDescriptionWrapper>
              )}
              <Paragraph className="sectionTitle">{t('CHOOSE_A_LOCATION')}</Paragraph>
              <St.HeaderScrollBox>
                {isLoading ? (
                  <Skeleton style={{ height: 36, width: '100%', borderRadius: 4 }} />
                ) : (
                  <>
                    {getFiltering(true)}
                    {getFiltering(false)}
                  </>
                )}
              </St.HeaderScrollBox>
              {!!filteredLocations.data.length && !filteredLocations.dataFiltered.length ? (
                <St.NoResultContainer>
                  <EmptyResults size={120} className="emptyResultsIcon" />
                  <Paragraph className="noResultTitle">{t('WE_ARE_SORRY')}</Paragraph>
                  <Paragraph className="noResultSubtitle">{t('NO_LOCATION_FOUND')}</Paragraph>
                  <button onClick={() => onSearch('')} type="button" className="seeAllLocationsButton">
                    {t('SEE_ALL_LOCATIONS')}
                  </button>
                </St.NoResultContainer>
              ) : (
                <St.CardLocalWrapper>
                  <St.CardsWrapper>
                    {isLoading ? (
                      <>
                        {[...Array(4).keys()].map((el) => (
                          <CardLocalItemSkeleton key={el} />
                        ))}
                      </>
                    ) : (
                      <>
                        {filteredLocations.dataFiltered.map((location) => (
                          <CardLocalItem
                            key={location.id}
                            setSelectedLocation={setSelectedLocation}
                            isSelected={!!(location.id === selectedLocation?.id)}
                            location={location}
                            queryParams={queryParams}
                            onClickOverride={
                              location.id === RM_GROUP_CARD_ID
                                ? () =>
                                    routerPush(
                                      `/${countryCode}/region/metropolitana${queryParams ? `/${queryParams}` : ''}`,
                                    )
                                : undefined
                            }
                          />
                        ))}
                      </>
                    )}
                  </St.CardsWrapper>
                </St.CardLocalWrapper>
              )}
            </St.CardsAndMapWrapper>
          </GlobalContentLayout>
        </St.Container>
      </PageProvider>
    </>
  );
};

export { getLocationsServerSideProps as getServerSideProps } from './getServerSideProps';

export default Locations;
